import type { LegalSection } from '../types/legal'

export const PRIVACY_UPDATED = 'September 28, 2026'

export const PRIVACY_NOTE =
  'MeetSynthia.ai operates at the instruction layer only—we do not generate AI outputs. All answers and content are produced by frontier AI models (OpenAI, Anthropic, Google, etc.). Enterprises retain full ownership of their data. Your proprietary information is never shared with third parties or other customers.'

export const PRIVACY: LegalSection[] = [
  {
    h: '1. Overview',
    body: [
      {
        t: 'p',
        x: 'MeetSynthia, Inc. ("Synthia," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and protect information related to your use of our website and Platform (collectively, the "Services"). This Policy applies to all users of MeetSynthia.ai, including enterprise clients, individual users, and visitors to our website.',
      },
    ],
  },
  {
    h: '2. Information We Collect',
    body: [
      { t: 'p', x: 'We collect the following categories of information:' },
      { t: 'h3', x: 'Account Data' },
      {
        t: 'ul',
        items: [
          'Name, email address, organization name, job title/role.',
          'Login credentials (securely hashed and encrypted).',
          'Billing and payment information (processed through third-party payment processors).',
        ],
      },
      { t: 'h3', x: 'Usage Data' },
      {
        t: 'ul',
        items: [
          'System logs, error reports, and performance telemetry.',
          'API latency, transmission timestamps, and active guardrail configuration selections.',
          'Feature adoption and engagement metrics.',
          'Browser type, IP address, device information, and access times.',
        ],
      },
      { t: 'h3', x: 'Prompt Metadata and Configurations' },
      {
        t: 'ul',
        items: [
          'Text inputs and prompt structures necessary for guardrail operations.',
          'Custom guardrails, roles, attributes, and workflow configurations.',
          'Institutional knowledge and domain-specific rules you create.',
          'Audit logs of prompt transformations and governance actions.',
        ],
      },
      { t: 'h3', x: 'Cookies and Analytics' },
      {
        t: 'ul',
        items: [
          'Non-identifiable technical information to improve service performance.',
          'Session management and authentication tokens.',
          'Analytics data to understand usage patterns and optimize features.',
        ],
      },
      { t: 'h3', x: 'We do NOT collect:' },
      {
        t: 'ul',
        items: [
          'Content of final AI outputs (these remain with you and your chosen AI provider).',
          'Sensitive personal information unless specifically required for service delivery.',
          'Continuous data from your downstream interactions with third-party AI models.',
        ],
      },
      { t: 'h3', x: 'We do NOT use your data to:' },
      {
        t: 'ul',
        items: [
          'Train third-party AI models.',
          'Build external datasets or benchmark corpora.',
          'Market to other customers.',
          'Develop competing products or services.',
        ],
      },
    ],
  },
  {
    h: '3. How We Use Information',
    body: [
      { t: 'p', x: 'We use collected information to:' },
      {
        t: 'ul',
        items: [
          [
            'Service Delivery:',
            'Provide, operate, and maintain the Platform; process and optimize prompt instructions through our instruction layer; apply guardrails, governance rules, and compliance controls; enable integrations with your chosen AI providers.',
          ],
          [
            'Service Improvement:',
            'Identify and resolve technical issues, optimize guardrail effectiveness and pre-flight response times, and analyze aggregate technical performance to improve product features.',
          ],
          [
            'Compliance and Security:',
            'Maintain audit logs for compliance and debugging; detect and prevent fraud, abuse, or security threats; comply with legal obligations; and enforce our Terms and Conditions.',
          ],
          [
            'Communication:',
            'Send service updates, security alerts, and product announcements; respond to support requests; provide training and onboarding assistance; and send marketing communications (with your explicit consent and opt-out options).',
          ],
          [
            'Research and Development:',
            'Develop improved governance patterns and best practices using anonymized, aggregated data to enhance enterprise security, privacy, and compliance capabilities.',
          ],
        ],
      },
    ],
  },
  {
    h: '4. Legal Basis for Processing (GDPR)',
    body: [
      {
        t: 'p',
        x: 'For users in the European Economic Area (EEA), United Kingdom, or Switzerland, we process personal data based on the following legal grounds:',
      },
      {
        t: 'ul',
        items: [
          ['Contract Necessity:', 'Processing is necessary to provide the Services you requested and to fulfill our contractual obligations.'],
          [
            'Legitimate Interests:',
            'We have legitimate interests in maintaining and improving the Platform, ensuring security, preventing fraud, and conducting business operations, provided these interests do not override your fundamental rights and freedoms.',
          ],
          [
            'Consent:',
            'For optional features such as marketing communications, beta programs, or analytics, we obtain explicit consent which you may withdraw at any time.',
          ],
          ['Legal Obligation:', 'Processing is necessary to comply with applicable laws, regulations, court orders, or governmental requests.'],
        ],
      },
      {
        t: 'p',
        x: 'You have the right to object to processing based on legitimate interests. Contact us at privacy@meetsynthia.ai to exercise this right.',
      },
    ],
  },
  {
    h: '5. Client Data Ownership & Environment Preference',
    body: [
      { t: 'h3', x: 'Absolute Client Ownership' },
      {
        t: 'ul',
        items: [
          [
            'Your Data is Entirely Yours:',
            'All company data, prompts, inputs, custom guardrails, and proprietary configurations belong completely and exclusively to you.',
          ],
          [
            'Business-Specific Continuity:',
            'You retain absolute ownership over all data and configurations generated specifically for your business. Synthia does not own, claim rights to, lease, or retain any business-specific data generated through your use of the platform.',
          ],
        ],
      },
      { t: 'h3', x: 'Deployment Model and Environmental Preference' },
      {
        t: 'p',
        x: 'To guarantee absolute data sovereignty, Synthia explicitly prefers to deploy and run directly inside your own infrastructure (self-hosted, on-premises, or within your secure Virtual Private Cloud (VPC)).',
      },
      {
        t: 'ul',
        items: [
          [
            'Inside Your Environment:',
            'When deployed in your infrastructure, you maintain full, exclusive control, hosting, and custody of all data, code, configurations, and system logs. Synthia cannot access, view, or retain any telemetry or prompt data unless you explicitly grant written access for localized troubleshooting.',
          ],
          [
            'Synthia Cloud Environment:',
            'For clients who choose our secure managed cloud environment instead, your data is isolated, strictly siloed, and fully encrypted using industry-standard protocols (AES-256 for data at rest, TLS 1.3 for data in transit).',
          ],
        ],
      },
      { t: 'h3', x: 'Data Retention Periods' },
      { t: 'p', x: 'We retain data only as long as necessary to provide Services and comply with legal obligations:' },
      {
        t: 'ul',
        items: [
          ['Active account data:', 'Retained while your account is active.'],
          [
            'Audit logs and metadata:',
            "For clients utilizing Synthia's managed cloud environment, these are retained for 90 days after account termination for debugging purposes. For our preferred model—private VPC or self-hosted deployments—all audit logs remain completely within your local infrastructure and are subject exclusively to your internal retention rules.",
          ],
          ['Billing records:', 'Retained for 7 years to comply with financial regulations.'],
          ['Anonymized analytics:', 'May be retained indefinitely in aggregated form.'],
        ],
      },
      {
        t: 'p',
        x: 'Upon account termination, we will securely delete or return all managed customer data within 30 days and permanently remove all personally identifiable information from our cloud systems.',
      },
    ],
  },
  {
    h: '6. Data Sharing and Disclosure',
    body: [
      {
        t: 'p',
        x: 'We do NOT sell, rent, trade, or share your personal information or proprietary business data. We may share limited, non-proprietary technical information only with verified Service Providers (such as cloud infrastructure hosting or secure payment processors) who are bound by strict confidentiality agreements.',
      },
      { t: 'p', x: 'We implement strict safeguards to protect your proprietary information:' },
      {
        t: 'ul',
        items: [
          'Client-specific guardrails, workflows, and institutional knowledge are completely confidential and are never shared with other customers or third parties.',
          'Access to client data is restricted to authorized personnel on a strict, audited, need-to-know basis.',
          'While your proprietary configurations remain completely confidential, we may develop separate, generalized marketplace components based on universal industry best practices and independent research, ensuring any components are sufficiently abstracted to remove all client-specific business logic or trade secrets.',
        ],
      },
    ],
  },
  {
    h: '7. International Data Transfers',
    body: [
      {
        t: 'p',
        x: 'If you are located outside the United States, your information may be transferred to and processed in the United States or other countries where our service providers operate. For transfers from the EEA, UK, or Switzerland, we use approved safeguards including Standard Contractual Clauses (SCCs), Data Processing Agreements (DPAs) with explicit privacy commitments, and industry-standard encryption protocols to ensure full compliance with GDPR and UK GDPR.',
      },
    ],
  },
  {
    h: '8. Your Privacy Rights',
    body: [
      { t: 'p', x: 'Under GDPR, CCPA, and similar laws, you have the following rights:' },
      {
        t: 'ul',
        items: [
          ['Right to Access', 'and receive a copy of your personal data.'],
          ['Right to Rectification', 'of inaccurate or incomplete information.'],
          ['Right to Erasure ("Right to Be Forgotten")', 'subject to legal retention rules.'],
          ['Right to Restriction or Objection', 'to data processing.'],
          ['Right to Data Portability', 'in a structured, machine-readable format.'],
          ['Right to Non-Discrimination', 'for exercising your privacy rights.'],
        ],
      },
      {
        t: 'p',
        x: 'To exercise any of these rights, contact us at: privacy@meetsynthia.ai. We will respond to your verified request within 30 days.',
      },
    ],
  },
  {
    h: '9. Enterprise Data Control',
    body: [
      { t: 'p', x: 'For enterprise clients operating under a Data Processing Agreement (DPA):' },
      {
        t: 'ul',
        items: [
          [
            'Data Controller vs. Data Processor:',
            'The enterprise client is the data controller responsible for determining purposes and means of processing. Synthia acts strictly as a data processor, processing data solely according to your written instructions.',
          ],
          [
            "Synthia's Commitments:",
            'We process data only as instructed by the client, implement rigorous technical security measures, assist with data subject requests, notify clients of any cloud-based data breaches within 72 hours, and delete or return all client data upon termination.',
          ],
          [
            'Subprocessors:',
            'We maintain a list of approved subprocessors (available upon request) and will notify clients of any changes with an opportunity to object.',
          ],
        ],
      },
    ],
  },
  {
    h: '10. Security Measures',
    body: [
      { t: 'p', x: 'We implement a comprehensive SOC 2 Type II aligned security framework to protect your information, featuring:' },
      {
        t: 'ul',
        items: [
          [
            'Technical Safeguards:',
            'Full encryption of data in transit (TLS 1.3) and at rest (AES-256), multi-factor authentication (MFA), role-based access control (RBAC), and regular automated vulnerability scanning.',
          ],
          [
            'Organizational Safeguards:',
            'Mandatory background checks, continuous security awareness training, and comprehensive incident response procedures.',
          ],
        ],
      },
    ],
  },
  {
    h: "11. Children's Privacy",
    body: [
      {
        t: 'p',
        x: 'Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from minors. If you believe we have inadvertently collected information from a minor, contact us immediately at privacy@meetsynthia.ai.',
      },
    ],
  },
  {
    h: '12. Cookies and Tracking Technologies',
    body: [
      {
        t: 'p',
        x: 'We use cookies to maintain secure sessions, remember your preferences, and analyze anonymized technical usage patterns to optimize platform delivery. You can control cookie preferences through your individual browser settings, though disabling essential cookies may limit certain platform functions.',
      },
    ],
  },
  {
    h: '13. Updates to This Policy',
    body: [
      {
        t: 'p',
        x: 'We may update this Privacy Policy periodically to reflect changes in our platform features, security standards, or international regulations. When material changes occur, we will post the updated policy with a revised "Last Updated" date and notify active account administrators via email.',
      },
    ],
  },
  {
    h: '14. Contact Us',
    body: [
      { t: 'p', x: 'For questions, concerns, or requests regarding this Privacy Policy:' },
      { t: 'ul', items: ['privacy@meetsynthia.ai', 'legal@meetsynthia.ai (for DPA requests)'] },
      { t: 'p', x: ['Mailing Address:', 'MeetSynthia, Inc. Wilmington, Delaware, USA'] },
      { t: 'p', x: ['Data Protection Officer (for GDPR inquiries):', 'dpo@meetsynthia.ai'] },
    ],
  },
  {
    h: '15. Supervisory Authority',
    body: [
      {
        t: 'p',
        x: 'If you are located in the EEA, UK, or Switzerland, you have the right to lodge a complaint with your local data protection authority if you believe we have violated your privacy rights.',
      },
    ],
  },
]
